import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { AppShell } from './AppShell.tsx';
import type { TrailSelectionUrlState } from './browser-trail-selection.ts';
import type { CameraNavigation } from './camera-navigation.ts';
import type { TrailStatusController } from './trail-status.ts';
import type { SupportedLocale } from '../localization/locale.ts';
import type { LocaleUrlState } from '../localization/url-state.ts';
import type { MapEngineAdapter, MapMountOptions } from '../map/contracts.ts';

export interface ApplicationMountOptions {
  readonly adapter?: MapEngineAdapter;
  readonly locale?: SupportedLocale;
  readonly localeUrlState?: LocaleUrlState;
  readonly cameraNavigation?: CameraNavigation;
  readonly trailSelectionUrlState?: TrailSelectionUrlState;
  readonly trailStatusController?: TrailStatusController;
  readonly startupFailure?: boolean;
  readonly map?: MapMountOptions;
}

export function mountApplication(host: HTMLElement, options?: ApplicationMountOptions): () => void {
  const locale = options?.locale ?? 'de';
  const map =
    options?.adapter === undefined || options.map === undefined
      ? undefined
      : {
          adapter: options.adapter,
          options: {
            ...options.map,
            locale,
            initialView:
              options.cameraNavigation?.initialView(options.map.initialView) ??
              options.map.initialView,
          },
        };
  const root = createRoot(host);
  root.render(
    <StrictMode>
      <AppShell
        locale={locale}
        {...(options?.localeUrlState === undefined
          ? {}
          : { localeUrlState: options.localeUrlState })}
        {...(options?.cameraNavigation === undefined
          ? {}
          : { cameraNavigation: options.cameraNavigation })}
        {...(options?.trailSelectionUrlState === undefined
          ? {}
          : { trailSelectionUrlState: options.trailSelectionUrlState })}
        {...(options?.trailStatusController === undefined
          ? {}
          : { trailStatusController: options.trailStatusController })}
        startupFailure={options?.startupFailure ?? false}
        map={map}
        onMapStatusChange={(status) => {
          host.dataset['mapStatus'] = status;
        }}
      />
    </StrictMode>,
  );
  return () => {
    root.unmount();
    delete host.dataset['mapStatus'];
  };
}
